import { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { Link, type LinkProps } from '@tanstack/react-router';

import * as m from '@/paraglide/messages.js';
import { cn } from '@/utils/cn';

import { LocaleSelector } from '../common/LocaleSelector';

export function TopBar() {
  const [menuOpen, setMenuOpen] = useState(false);

  const NAV_LINKS: { to: LinkProps['to']; label: string }[] = [
    { to: '/{-$locale}/hospitals', label: m.nav_companies() },
    { to: '/{-$locale}/job-offers', label: m.nav_job_offers() },
    { to: '/{-$locale}/internships', label: m.nav_internships() },
    { to: '/{-$locale}/ratings', label: m.nav_ratings() },
    { to: '/{-$locale}/articles', label: m.nav_articles() },
    { to: '/{-$locale}/events', label: m.nav_events() },
  ];

  const navLinkBase = 'mb-2 lg:mb-0 block lg:border-0';
  const activeClass = cn(
    navLinkBase,
    'text-primary-500 underline underline-offset-2 decoration-solid font-medium',
  );
  const inactiveClass = cn(
    navLinkBase,
    'hover:text-primary-500 hover:underline hover:decoration-solid hover:underline-offset-2 transition-transform hover:scale-105 lg:p-0',
  );

  return (
    <nav className="sticky top-0 z-40 border-b border-gray-100 bg-white py-6 shadow-sm md:min-h-[112px]">
      <div className="mx-auto flex max-w-screen-xl flex-wrap justify-between px-4 lg:items-center">
        {/* Logo */}
        <Link to="/{-$locale}" className="flex items-center">
          <img
            src="/logo.png"
            alt="Pracuj ve Zdravotnictví"
            className="ml-2 h-10 md:ml-0 xl:h-16"
          />
        </Link>

        {/* Mobile: hamburger button */}
        <div className="flex flex-row lg:hidden">
          <IconButton onClick={() => setMenuOpen((o) => !o)} size="large">
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>

          {/* Mobile dropdown */}
          {menuOpen && (
            <div className="absolute top-full left-0 z-50 w-full border-t border-gray-100 bg-white shadow-lg">
              <ul className="mt-4 ml-5 flex flex-col space-y-2 pb-4 text-[16px]">
                {NAV_LINKS.map(({ to, label }) => (
                  <li key={to} onClick={() => setMenuOpen(false)}>
                    <Link
                      to={to}
                      activeProps={{ className: activeClass }}
                      inactiveProps={{ className: inactiveClass }}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="ml-4 pb-4">
                <LocaleSelector />
              </div>
            </div>
          )}
        </div>

        {/* Desktop navigation */}
        <div className="hidden lg:block">
          <div className="flex items-center gap-6">
            <ul className="ml-4 flex flex-row space-x-6 text-sm sm:text-[16px] xl:ml-0">
              {NAV_LINKS.map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    activeProps={{ className: activeClass }}
                    inactiveProps={{ className: inactiveClass }}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>

            <Button variant="contained" color="primary">
              {m.nav_sign_in()}
            </Button>

            <LocaleSelector />
          </div>
        </div>
      </div>
    </nav>
  );
}
